import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Form, Modal, ListGroup, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import {
  postMarketingEmailLists,
  putMarketingEmailListsId,
  deleteMarketingEmailListsId,
  postMarketingEmailListsIdSync,
  postMarketingEmailSync,
  useGetMarketingEmailLists,
  getGetMarketingEmailListsQueryKey,
  postMarketingEmailListsIdContacts,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '../contexts/NotificationContext';

const EmailListsTab: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [selectedList, setSelectedList] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sourceListIds, setSourceListIds] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const queryClient = useQueryClient();
  const { activeSyncs } = useNotifications();

  // Get sync status for a specific list
  const getListSyncStatus = (listId: string) => {
    // Find any sync event for this list (integration_id matches list id)
    for (const [, syncEvent] of activeSyncs) {
      if (syncEvent.integration_id === listId) {
        return syncEvent;
      }
    }
    return null;
  };

  // Check if a list is currently syncing
  const isListSyncing = (listId: string): boolean => {
    const syncEvent = getListSyncStatus(listId);
    return !!(syncEvent && (syncEvent.status === 'started' || syncEvent.status === 'progress'));
  };

  // Get tooltip content for sync button
  const getSyncTooltip = (listId: string) => {
    const syncEvent = getListSyncStatus(listId);
    if (!syncEvent) return null;

    switch (syncEvent.status) {
      case 'started':
        return 'Starting sync...';
      case 'progress':
        return syncEvent.progress?.message || 'Syncing...';
      case 'completed':
        return `Completed in ${((syncEvent.duration_ms || 0) / 1000).toFixed(1)}s`;
      case 'failed':
        return `Failed: ${syncEvent.error || 'Unknown error'}`;
      default:
        return null;
    }
  };

  // Fetch all lists
  const { data: lists, isLoading, error: queryError } = useGetMarketingEmailLists();

  // Refetch when component mounts (tab becomes active)
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rename list mutation
  const renameListMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      putMarketingEmailListsId(id, { name }),
    onSuccess: () => {
      setSuccess('Email list renamed successfully');
      setError(null);
      setShowRenameModal(false);
      setSelectedList(null);
      setNewListName('');
      queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Failed to rename email list');
      setSuccess(null);
    },
  });

  // Delete list mutation
  const deleteListMutation = useMutation({
    mutationFn: (id: string) => deleteMarketingEmailListsId(id),
    onSuccess: () => {
      setSuccess('Email list deleted successfully');
      setError(null);
      setShowDeleteConfirm(false);
      setSelectedList(null);
      queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Failed to delete email list');
      setSuccess(null);
    },
  });

  // Sync list mutation - just schedules the sync, progress comes via SSE
  const syncListMutation = useMutation({
    mutationFn: (id: string) => postMarketingEmailListsIdSync(id),
    onSuccess: (data) => {
      // Sync is scheduled, progress will come via SSE
      setSuccess(data.message || 'Sync scheduled');
      setError(null);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Failed to schedule sync');
      setSuccess(null);
    },
  });

  // Full sync mutation - syncs all lists and contacts to SendGrid
  const fullSyncMutation = useMutation({
    mutationFn: () => postMarketingEmailSync(),
    onSuccess: (data) => {
      setSuccess(data.message || 'Full sync scheduled');
      setError(null);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Failed to schedule full sync');
      setSuccess(null);
    },
  });

  const handleSyncList = (listId: string) => {
    setError(null);
    setSuccess(null);
    syncListMutation.mutate(listId);
  };

  const handleFullSync = () => {
    setError(null);
    setSuccess(null);
    fullSyncMutation.mutate();
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;

    setIsCreating(true);
    setError(null);

    try {
      // Step 1: Create the list
      const newList = await postMarketingEmailLists({ name: newListName.trim() });

      // Step 2: If source lists were selected, copy contacts from them
      if (sourceListIds.length > 0 && newList.id) {
        await postMarketingEmailListsIdContacts(newList.id, { list_ids: sourceListIds });
      }

      setSuccess('Email list created successfully' + (sourceListIds.length > 0 ? ' with contacts from selected lists' : ''));
      setShowCreateModal(false);
      setNewListName('');
      setSourceListIds([]);
      queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create email list');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRenameList = () => {
    if (selectedList && newListName.trim()) {
      renameListMutation.mutate({ id: selectedList.id, name: newListName.trim() });
    }
  };

  const handleDeleteList = () => {
    if (selectedList) {
      deleteListMutation.mutate(selectedList.id);
    }
  };

  const openRenameModal = (list: { id: string; name: string }) => {
    setSelectedList(list);
    setNewListName(list.name);
    setShowRenameModal(true);
  };

  const openDeleteConfirm = (list: { id: string; name: string }) => {
    setSelectedList(list);
    setShowDeleteConfirm(true);
  };

  return (
    <div className="p-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-end align-items-center gap-2">
            <Button
              variant="primary"
              onClick={handleFullSync}
              disabled={fullSyncMutation.isPending}
            >
              {fullSyncMutation.isPending ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Syncing...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-2"></i>
                  Sync Lists &amp; Contacts
                </>
              )}
            </Button>
            <Button variant="success" onClick={() => setShowCreateModal(true)}>
              <i className="bi bi-plus-circle me-2"></i>
              Create New List
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-folder me-2"></i>
                Your Lists
              </h5>
            </Card.Header>
            <Card.Body>
              {isLoading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {!isLoading && lists && lists.length === 0 && (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-inbox display-1 d-block mb-3"></i>
                  <p>No email lists yet. Create your first list to get started!</p>
                </div>
              )}

              {!isLoading && lists && lists.length > 0 && (
                <ListGroup>
                  {lists.map((list) => (
                    <ListGroup.Item
                      key={list.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <i className="bi bi-folder-fill me-2 text-primary"></i>
                        <strong>{list.name}</strong>
                        <span className="badge bg-secondary ms-2">
                          {list.contact_count ?? 0} {(list.contact_count ?? 0) === 1 ? 'contact' : 'contacts'}
                        </span>
                      </div>
                      <div>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            getSyncTooltip(list.id) ? (
                              <Tooltip id={`sync-tooltip-${list.id}`}>
                                {getSyncTooltip(list.id)}
                              </Tooltip>
                            ) : <></>
                          }
                        >
                          <span className="d-inline-block">
                            <Button
                              variant={
                                getListSyncStatus(list.id)?.status === 'failed'
                                  ? 'outline-danger'
                                  : getListSyncStatus(list.id)?.status === 'completed'
                                  ? 'outline-success'
                                  : 'outline-success'
                              }
                              size="sm"
                              className="me-2"
                              onClick={() => handleSyncList(list.id)}
                              disabled={isListSyncing(list.id)}
                            >
                              {isListSyncing(list.id) ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-1"
                                  />
                                  Syncing...
                                </>
                              ) : getListSyncStatus(list.id)?.status === 'completed' ? (
                                <>
                                  <i className="bi bi-check-circle me-1"></i>
                                  Synced
                                </>
                              ) : getListSyncStatus(list.id)?.status === 'failed' ? (
                                <>
                                  <i className="bi bi-x-circle me-1"></i>
                                  Failed
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-cloud-upload me-1"></i>
                                  Sync
                                </>
                              )}
                            </Button>
                          </span>
                        </OverlayTrigger>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openRenameModal({ id: list.id, name: list.name })}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Rename
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => openDeleteConfirm({ id: list.id, name: list.name })}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create List Modal */}
      <Modal show={showCreateModal} onHide={() => {
        setShowCreateModal(false);
        setSourceListIds([]);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Email List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>List Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., VIP Customers"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateList();
                  }
                }}
                autoFocus
              />
            </Form.Group>

            {lists && lists.length > 0 && (
              <Form.Group>
                <Form.Label>Copy contacts from existing lists (optional)</Form.Label>
                <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {lists.map((list) => (
                    <Form.Check
                      key={list.id}
                      type="checkbox"
                      id={`source-list-${list.id}`}
                      label={
                        <span>
                          {list.name}
                          <span className="text-muted ms-2">({list.contact_count ?? 0})</span>
                        </span>
                      }
                      checked={sourceListIds.includes(list.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSourceListIds([...sourceListIds, list.id]);
                        } else {
                          setSourceListIds(sourceListIds.filter(id => id !== list.id));
                        }
                      }}
                    />
                  ))}
                </div>
                <Form.Text className="text-muted">
                  Contacts from selected lists will be added to the new list.
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreateModal(false);
            setSourceListIds([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateList}
            disabled={!newListName.trim() || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create List'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Rename List Modal */}
      <Modal show={showRenameModal} onHide={() => setShowRenameModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Rename Email List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>New List Name</Form.Label>
              <Form.Control
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRenameList();
                  }
                }}
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRenameModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRenameList}
            disabled={!newListName.trim() || renameListMutation.isPending}
          >
            {renameListMutation.isPending ? 'Renaming...' : 'Rename List'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Email List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <strong>Warning:</strong> This action cannot be undone.
          </Alert>
          <p>
            Are you sure you want to delete the list <strong>"{selectedList?.name}"</strong>?
          </p>
          <p className="text-muted small mb-0">
            Contacts in this list will not be deleted, only the list itself.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteList}
            disabled={deleteListMutation.isPending}
          >
            {deleteListMutation.isPending ? 'Deleting...' : 'Delete List'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailListsTab;
